// src/components/Seo.jsx
import React, { useEffect } from "react";

// The static tags in public/index.html (description, robots) are the
// defaults that link unfurlers without JavaScript read. A page's own value
// goes INTO that static tag (its original value is restored when the page
// goes away), so the head never carries two tags of the same name; before,
// every page appended a second description and robots tag after the static
// one. A tag with no static counterpart is created and removed on cleanup,
// as before.
function reuseStatic(selector, attr, restoredAttrs) {
  const el = document.head.querySelector(`${selector}:not([data-seo])`);
  if (el) restoredAttrs.push({ el, attr, value: el.getAttribute(attr) });
  return el;
}

function upsertMeta(attrName, attrValue, content, createdNodes, restoredAttrs) {
  if (!content) return;
  const selector = `meta[${attrName}="${attrValue}"]`;
  let el =
    document.head.querySelector(`${selector}[data-seo="true"]`) ||
    reuseStatic(selector, "content", restoredAttrs);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    el.setAttribute("data-seo", "true");
    createdNodes.push(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href, createdNodes, restoredAttrs) {
  if (!href) return;
  const selector = `link[rel="${rel}"]`;
  let el =
    document.head.querySelector(`${selector}[data-seo="true"]`) ||
    reuseStatic(selector, "href", restoredAttrs);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("data-seo", "true");
    createdNodes.push(el);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertScriptJsonLd(id, obj, createdNodes) {
  if (!obj) return;
  let el = document.head.querySelector(`script[type="application/ld+json"][id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.setAttribute("data-seo", "true");
    createdNodes.push(el);
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(obj);
}

export default function SEO({
  title,
  description,
  canonical,
  robots = "index,follow",
  url,               // absolute URL of this route
  image,             // absolute URL to 1200x630 image
  siteName = "BossDrop",
  type = "website",  // for OG
  twitterCard = "summary_large_image",
  twitterSite = "@BossDropAI",
  jsonLd,            // object or array
  breadcrumbsJsonLd, // object (BreadcrumbList)
  extraMeta = [],    // [{ name, content }] or [{ property, content }]
}) {
  useEffect(() => {
    const created = [];
    // Static tags whose value this page replaced: { el, attr, value }.
    const restored = [];

    // Title
    const prevTitle = document.title;
    if (title) document.title = title;

    // Canonical
    if (canonical) upsertLink("canonical", canonical, created, restored);

    // Basic meta
    upsertMeta("name", "description", description, created, restored);
    upsertMeta("name", "robots", robots, created, restored);

    // Open Graph
    upsertMeta("property", "og:title", title, created, restored);
    upsertMeta("property", "og:description", description, created, restored);
    upsertMeta("property", "og:url", url || canonical, created, restored);
    upsertMeta("property", "og:type", type, created, restored);
    upsertMeta("property", "og:site_name", siteName, created, restored);
    upsertMeta("property", "og:image", image, created, restored);

    // Twitter / X
    upsertMeta("name", "twitter:card", twitterCard, created, restored);
    upsertMeta("name", "twitter:site", twitterSite, created, restored);
    upsertMeta("name", "twitter:title", title, created, restored);
    upsertMeta("name", "twitter:description", description, created, restored);
    upsertMeta("name", "twitter:image", image, created, restored);

    // Extra custom meta
    (extraMeta || []).forEach((m) => {
      if (m?.name) upsertMeta("name", m.name, m.content, created, restored);
      if (m?.property) upsertMeta("property", m.property, m.content, created, restored);
    });

    // JSON-LD
    if (jsonLd) upsertScriptJsonLd("seo-jsonld", jsonLd, created);
    if (breadcrumbsJsonLd) upsertScriptJsonLd("seo-breadcrumbs", breadcrumbsJsonLd, created);

    // Cleanup on unmount or prop change: remove only nodes we created, put
    // the static tags' own values back, restore the title.
    return () => {
      created.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
      for (let i = restored.length - 1; i >= 0; i -= 1) {
        const { el, attr, value } = restored[i];
        if (value === null) el.removeAttribute(attr);
        else el.setAttribute(attr, value);
      }
      if (title) document.title = prevTitle;
    };
  }, [
    title, description, canonical, robots, url, image, siteName, type,
    twitterCard, twitterSite, JSON.stringify(jsonLd), JSON.stringify(breadcrumbsJsonLd),
    JSON.stringify(extraMeta),
  ]);

  return null; // head only
}
